import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import moment from 'moment';
import ContentLoader from '../loader/ContentLoader';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { logsService } from '../../services/logs.service';
import { ERROR_LOG_FILE_BASE_URL } from '../../config/apiConfig';

const MySwal = withReactContent(Swal);

/* ---------- styles ---------- */
const sectionCard = { background: "#fff", borderRadius: 14, border: "1px solid #d5dbe8", padding: 20, marginBottom: 16 };
const labelStyle = { fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#4b5563", marginBottom: 2 };
const valueStyle = { fontSize: 14, fontWeight: 600, color: "#1a202c", wordBreak: "break-word" };
const monoStyle = { fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: 12 };
const codeBlock = {
  ...monoStyle, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
  background: "#1e1b3a", color: "#e4e1ff", borderRadius: 10, padding: 14, maxHeight: 360, overflow: "auto"
};
const pill = (bg, color) => ({
  display: "inline-block", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 12,
  background: bg, color, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap"
});
const btnLight = { background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" };

const SEVERITY_COLORS = {
  error: ["#fdecec", "#d64545"],
  warning: ["#fff4e6", "#e8590c"],
  info: ["#e7f5ff", "#1971c2"],
};
const METHOD_COLORS = {
  GET: ["#e6f7f1", "#0f8a5f"],
  POST: ["#e8f0fe", "var(--btn-bg)"],
  PUT: ["#fff4e6", "#e8590c"],
  PATCH: ["#f3ebfa", "var(--btn-hover-bg)"],
  DELETE: ["#fdecec", "#d64545"],
};

const formatDate = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm:ss.SSS A") : "-");
const toJson = (v) => (typeof v === "string" ? v : JSON.stringify(v ?? null, null, 2));
const isEmpty = (v) => v === undefined || v === null || v === "" || (typeof v === "object" && Object.keys(v).length === 0);

const copyText = (text) => {
  navigator.clipboard?.writeText(text)
    .then(() => toast.success("Copied to clipboard"))
    .catch(() => toast.error("Could not copy"));
};

const ErrorLogDetail = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState(null);

  useEffect(() => {
    loadDetail();
  }, [id]);

  /* ---------- API: details ---------- */
  const loadDetail = async () => {
    try {
      setLoading(true);
      const res = await logsService.errorDetails(id);
      if (res?.success) {
        setLog(res.resData);
      } else {
        setLog(null);
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  /* ---------- small render helpers ---------- */
  const sectionTitle = (icon, text, action) => (
    <div className="d-flex align-items-center justify-content-between mb-3 gap-2">
      <h6 className="m-0 d-flex align-items-center gap-2 fw-bold" style={{ fontSize: 14, color: "var(--btn-hover-bg)" }}>
        <i className={`bi ${icon}`} style={{ color: "var(--btn-bg)" }}></i> {text}
      </h6>
      {action}
    </div>
  );
  const copyBtn = (text) => (
    <button type="button" onClick={() => copyText(text)} className="btn btn-sm d-flex align-items-center gap-1 px-2"
      style={{ ...btnLight, height: 28, fontSize: 12 }}>
      <i className="bi bi-clipboard"></i> Copy
    </button>
  );
  const field = (label, value, mono) => (
    <div className="col-12 col-md-6 col-lg-4" key={label}>
      <div style={labelStyle}>{label}</div>
      <div style={mono ? { ...valueStyle, ...monoStyle, fontWeight: 600 } : valueStyle}>{isEmpty(value) ? "-" : value}</div>
    </div>
  );
  const jsonBlock = (label, value) => (
    <div className="mt-3" key={label}>
      <div className="d-flex align-items-center justify-content-between mb-1">
        <div style={labelStyle}>{label}</div>
        {!isEmpty(value) && copyBtn(toJson(value))}
      </div>
      {isEmpty(value)
        ? <div style={{ fontSize: 13, color: "#475569" }}>Empty</div>
        : <pre style={codeBlock}>{toJson(value)}</pre>}
    </div>
  );
  const severityPill = (s) => {
    const [bg, color] = SEVERITY_COLORS[s] || ["#f1f3f9", "#6b7a90"];
    return <span style={pill(bg, color)}>{s || "-"}</span>;
  };
  const methodPill = (m) => {
    const [bg, color] = METHOD_COLORS[m] || ["#f1f3f9", "#6b7a90"];
    return <span style={pill(bg, color)}>{m || "-"}</span>;
  };
  const statusColor = (code) => (code >= 500 ? "#d64545" : code >= 400 ? "#e8590c" : "#0f8a5f");

  const detail = log?.detail;
  const headers = detail?.request?.headers || {};
  const logFileUrl = log?.log_file ? `${ERROR_LOG_FILE_BASE_URL}/${encodeURIComponent(log.log_file)}` : "";

  return (
    <section className="section dashboard">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Error Log #{id}</h4>
          <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
            <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <Link to="/logs" style={{ color: "#475569" }}>Logs</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <Link to="/logs/error" style={{ color: "#475569" }}>Error Logs</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <span className="fw-semibold" style={{ color: "#4a5568" }}>#{id}</span>
          </nav>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button type="button" onClick={loadDetail} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={btnLight}>
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
          <Link to="/logs/error" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Error Logs
          </Link>
        </div>
      </div>

      {loading ? <ContentLoader loaderType="circleSpin" /> : !log ? (
        <div style={{ ...sectionCard, textAlign: "center", padding: 40 }}>
          <i className="bi bi-search" style={{ fontSize: 32, color: "#b4bdd0" }}></i>
          <div className="fw-semibold mt-2" style={{ color: "#4a5568" }}>Error log not found</div>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ ...sectionCard, borderLeft: `4px solid ${(SEVERITY_COLORS[log.severity] || [])[1] || "var(--btn-bg)"}` }}>
            <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
              {severityPill(log.severity)}
              <span style={{ fontSize: 13, fontWeight: 700, color: statusColor(log.status_code) }}>HTTP {log.status_code}</span>
              <span style={{ color: "#d0d5e2" }}>|</span>
              {methodPill(log.http_method)}
              <span style={{ ...monoStyle, fontSize: 13, color: "#1a202c" }}>{log.route}</span>
            </div>
            <div className="fw-bold" style={{ fontSize: 18, color: "#1a202c" }}>{log.error_name || "-"}</div>
            <div style={{ fontSize: 14, color: "#d64545", wordBreak: "break-word" }}>{log.error_message || "-"}</div>
            <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: 12, color: "#475569" }}>
              <span><i className="bi bi-clock me-1"></i>{formatDate(detail?.timestamp || log.created_at)}</span>
              {detail?.duration_ms != null && <span><i className="bi bi-stopwatch me-1"></i>{detail.duration_ms} ms</span>}
              <span><i className="bi bi-code-slash me-1"></i>{log.method_name}</span>
            </div>
          </div>

          {/* Overview */}
          <div style={sectionCard}>
            {sectionTitle("bi-info-circle-fill", "Overview", logFileUrl && (
              <a href={logFileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm d-flex align-items-center gap-1 px-2"
                style={{ ...btnLight, height: 28, fontSize: 12 }}>
                <i className="bi bi-file-earmark-code"></i> Open Log File
              </a>
            ))}
            <div className="row g-3">
              {field("Request ID", log.request_id, true)}
              {field("Method Name", log.method_name)}
              {field("Source", log.source)}
              {field("User ID", log.user_id)}
              {field("IP Address", log.ip_address, true)}
              {field("Log File", logFileUrl && (
                <a href={logFileUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--btn-bg)", textDecoration: "none" }}>
                  {log.log_file} <i className="bi bi-box-arrow-up-right" style={{ fontSize: 11 }}></i>
                </a>
              ), true)}
            </div>
          </div>

          {!log.detail_available || !detail ? (
            <div style={{ ...sectionCard, background: "#fff8ec", border: "1px solid #ffd8a8", color: "#a15c07", fontSize: 13 }}>
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              The full log file for this entry is not available, so request, response and stack details cannot be shown.
            </div>
          ) : (
            <>
              {/* Error + stack */}
              <div style={sectionCard}>
                {sectionTitle("bi-bug-fill", "Error", detail.error?.stack && copyBtn(detail.error.stack))}
                <div className="row g-3 mb-3">
                  {field("Name", detail.error?.name)}
                  {field("HTTP Code", detail.error?.httpCode)}
                  {field("Message", detail.error?.message)}
                </div>
                <div style={labelStyle}>Stack Trace</div>
                {detail.error?.stack
                  ? <pre style={{ ...codeBlock, color: "#ffb4b4" }}>{detail.error.stack}</pre>
                  : <div style={{ fontSize: 13, color: "#475569" }}>Empty</div>}
              </div>

              <div className="row g-3">
                {/* Request */}
                <div className="col-12 col-xl-6">
                  <div style={{ ...sectionCard, height: "calc(100% - 16px)" }}>
                    {sectionTitle("bi-box-arrow-in-right", "Request")}
                    <div className="row g-3">
                      {field("Method", detail.request?.method)}
                      {field("IP", detail.request?.ip, true)}
                      <div className="col-12">
                        <div style={labelStyle}>Path</div>
                        <div style={{ ...valueStyle, ...monoStyle }}>{detail.request?.path || "-"}</div>
                      </div>
                    </div>
                    {jsonBlock("Body", detail.request?.body)}
                    {jsonBlock("Query", detail.request?.query)}
                    {jsonBlock("Params", detail.request?.params)}
                  </div>
                </div>

                {/* Response */}
                <div className="col-12 col-xl-6">
                  <div style={{ ...sectionCard, height: "calc(100% - 16px)" }}>
                    {sectionTitle("bi-box-arrow-right", "Response")}
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div style={labelStyle}>Status</div>
                        <div style={{ ...valueStyle, color: statusColor(detail.response?.status) }}>{detail.response?.status ?? "-"}</div>
                      </div>
                    </div>
                    {jsonBlock("Body", detail.response?.body)}
                    {jsonBlock("User", detail.user)}
                  </div>
                </div>
              </div>

              {/* Headers */}
              <div style={sectionCard}>
                {sectionTitle("bi-list-columns-reverse", `Request Headers (${Object.keys(headers).length})`, !isEmpty(headers) && copyBtn(toJson(headers)))}
                {isEmpty(headers) ? <div style={{ fontSize: 13, color: "#475569" }}>Empty</div> : (
                  <div className="table-responsive" style={{ border: "1px solid #eef1f6", borderRadius: 10 }}>
                    <table className="table table-sm align-middle mb-0">
                      <tbody>
                        {Object.entries(headers).map(([key, value]) => (
                          <tr key={key}>
                            <td style={{ ...monoStyle, color: "#6b7a90", whiteSpace: "nowrap", width: "1%", padding: "8px 12px", background: "#f8f9fe" }}>{key}</td>
                            <td style={{ ...monoStyle, color: "#1a202c", wordBreak: "break-all", padding: "8px 12px" }}>{String(value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}

export default ErrorLogDetail;
